import { Breadcrumb, Button, Card, Result, Spin, Tag } from 'antd';
import { capitalize } from 'lodash';
import { Fragment, useContext, useEffect, useState } from 'react';
import gql from 'graphql-tag';
import ErrorMessage from '../ErrorMessage';
import ClassAvatar from '../images/ClassAvatar';
import AssignmentsContext from '../../lib/AssignmentsContext';
import { useQuery, useReportsAccess } from '../../lib/hooks';
import { UI_CLASS_QUERY } from '../../helpers/gqlQueries/ui/tree';
const sorter = (a, b) => a.sortName < b.sortName ? -1 : 1;
const displayEntities = (arr, onClick) => arr.map(e => ({
  ...e,
  name: e.__typename !== 'Class' ? e.name : `${e.name} (${e.semester}, ${e.grade})`,
  sortName: e.__typename !== 'Class' ? e.name : `${e.name} (${e.semester}, ${e.grade})`,
  displayName: e.__typename !== 'Class' ? e.name : <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }}>
        <ClassAvatar of={e} />
        <div style={{
      textAlign: 'left'
    }}>
          <span children={e.name} />
          <aside style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 20
      }}>
            <small style={{
          color: '#8C8C8C',
          fontWeight: 'normal'
        }}>
              Semester: {e.semester}
            </small>
            <small style={{
          color: '#8C8C8C',
          fontWeight: 'normal'
        }}>
              Grade: {e.grade}
            </small>
            <small style={{
          color: '#8C8C8C',
          fontWeight: 'normal'
        }}>
              Class Type: {capitalize(e.type)}
            </small>
          </aside>
        </div>
      </div>
})).sort(sorter).flatMap(e => [<Button type="link" onClick={() => onClick({
  id: e.id,
  name: e.name,
  type: e.__typename
})} children={e.displayName} key={`btn-${e.__typename}:${e.id}`} />, <br key={`br-${e.__typename}:${e.id}`} />]).slice(0, -1);
const MyStates = ({
  onClick
}) => {
  const {
    loading,
    error,
    data
  } = useQuery(gql`
    query UI_MY_STATES {
      states {
        id
        name
      }
    }
  `);
  if (loading) {
    return <Spin size="large" />;
  }
  if (error) {
    return <ErrorMessage message="Error loading data" />;
  }
  return displayEntities(data.states, onClick);
};
const MySubEntities = ({
  id,
  entity,
  subEntities,
  onClick
}) => {
  const {
    loading,
    error,
    data
  } = useQuery(gql`
      query UI_MY_${entity.toUpperCase()}($id: ID!) {
        ${entity}ById(id: $id) {
          id
          ${subEntities} {
            id
            name
            ${subEntities === 'classes' ? 'avatarImageUrl, avatarBorderColor, avatarBackgroundColor, semester, grade, current, type' : ''}
          }
        }
      }
    `, {
    variables: {
      id
    }
  });
  if (loading) {
    return <Spin size="large" />;
  }
  if (error) {
    return <ErrorMessage message="Error loading data" />;
  }
  const entities = data[`${entity}ById`]?.[subEntities]?.filter(klass => subEntities !== 'classes' || klass.current)?.sort(sorter);
  return entities?.length > 0 ? displayEntities(entities, onClick) : `No ${subEntities}`;
};
const configs = [{
  entity: 'state',
  subEntities: 'districts'
}, {
  entity: 'district',
  subEntities: 'schools'
}, {
  entity: 'school',
  subEntities: 'classes'
}];
const ClassPicker = () => {
  const {
    data
  } = useContext(AssignmentsContext);
  const menuConfig = useReportsAccess().menu;
  const [classID, setClassID] = useState();
  const [breadcrumbs, setBreadcrumbs] = useState([menuConfig.topLevel]);
  const location = breadcrumbs[breadcrumbs.length - 1];
  const config = configs.find(c => c.entity === String(location.type).toLowerCase());
  const addBreadcrumb = obj => setBreadcrumbs([...breadcrumbs, obj]);
  const reduceBreadcrumbs = obj => setBreadcrumbs(breadcrumbs.slice(0, breadcrumbs.indexOf(obj) + 1));
  const {
    loading: classLoading,
    error: classError,
    data: classData
  } = useQuery(UI_CLASS_QUERY, {
    variables: {
      classId: classID
    },
    skip: !classID
  });
  useEffect(() => {
    if (classID && !classLoading && !classError && classData) {
      data.klass = classData.classById;
    }
  }, [classID, classLoading, classError, classData]);
  if (classID) {
    if (classLoading) {
      return <Result title="Fetching students..." icon={<Spin size="large" />} />;
    }
    if (classError) {
      return <ErrorMessage message="Failed to fetch students." />;
    }
    if (!(classData.classById.students?.length > 0)) {
      return <Result title="Empty Class" subTitle="The selected class has no students." status="warning" extra={<Button onClick={() => setClassID(null)}>Select different one</Button>} />;
    }
    return <Result title="Students fetched." status="success" />;
  }
  return <>
      <h3 className="step-title">Choose a Class</h3>
      <div className="leaderboard-breadcrumbs">
        <Breadcrumb data-sentry-element="Breadcrumb" data-sentry-source-file="ClassPicker.jsx">
        {breadcrumbs.map((item, index) => <Breadcrumb.Item key={`item-${index}`}>
              <Button type="link" onClick={() => reduceBreadcrumbs(item)}>
                {item.name}
              </Button>
            </Breadcrumb.Item>)}
        </Breadcrumb>
      </div>
      <Card bordered={false} data-sentry-element="Card" data-sentry-source-file="ClassPicker.jsx">
        {config ? <MySubEntities {...config} id={location.id} onClick={location.type === 'School' ? ({
        id
      }) => setClassID(id) : addBreadcrumb} /> : <MyStates onClick={addBreadcrumb} />}
      </Card>
    </>;
};
export default ClassPicker;